import animation from '@helpers/animation';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { ReactComponent as SvgPlayIcon } from '@img/icons/play-icon.svg';
import Badge from '@stories/Components/Badge/Badge';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import ParagraphStyles from '@stories/Components/Global/Typography/Paragraph.styles';
import styled from 'styled-components';
const Container = styled.article `
  display: block;
`;
const Link = styled.a `
  display: block;
  color: currentColor;
  text-decoration: none;

  &:focus {
    outline: none;
  }
`;
const ImageWrapper = styled.div `
  position: relative;
  display: block;
  padding-bottom: ${(425 / 560) * 100}%;
  [data-full-height='true'] & {
    padding-bottom: ${(850 / 560) * 100}%;
  }
`;
const Image = styled.img `
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: scale(1);
  transition: transform 1s ${animation.sharp};
  width: 100%;
  z-index: 1;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  [data-component-theme]:not([data-component-theme='${1 /* HomeStepper */}'])
    [data-full-height='false']
    ${Link}:focus
    &,
  [data-component-theme]:not([data-component-theme='${1 /* HomeStepper */}'])
    [data-full-height='false']
    ${Link}:hover
    & {
    transform: scale(0.7);
  }

  [data-component-theme='${1 /* HomeStepper */}'] [data-full-height='false'] & {
    border-radius: 0 0 70px 0;
  }
`;
const ContentWrapper = styled.div `
  display: block;
  padding: 24px 0 0;
  
  [data-image='false'] & {
    padding: 74px 0 0;
  }

  @media ${from(Device.TabletLarge)} {
    padding: 28px 0 0;
    [data-image='false'] & {
      padding: 78px 0 0;
    }
  }
`;
const Metadata = styled.div `
  display: flex;
  align-items: center;
  margin: 0 0 10px;

  @media ${from(Device.Tablet)} {
    margin: 0 0 24px;
  }
`;
const StyledBadge = styled(Badge) ``;
const Date = styled.time `
  display: inline-block;
  ${fonts.matter.regular};
  font-size: 14px;
  line-height: 17px;
  text-transform: uppercase;

  @media ${from(Device.TabletLarge)} {
    font-size: 16px;
    line-height: 24px;
  }

  ${StyledBadge} + &::before {
    content: '';
    border-left: 1px solid ${brand.black};
    margin: 0 8px;

    @media ${from(Device.DesktopSmall)} {
      margin: 0 16px;
    }
  }
`;
const Title = styled.h3 `
  display: block;
  ${HeadingStyles.H3Styles};
  margin: 0 0 18px;

  @media ${from(Device.Tablet)} {
    margin: 0 0 24px;
  }
`;
const Summary = styled.p `
  display: block;
  ${ParagraphStyles.LargeStyles};
  color: var(--textColor);

  [data-component-theme='${1 /* HomeStepper */}'] & {
    ${fonts.matter.medium};
  }

  @media ${from(Device.Tablet)} {
    margin: 0 0 20px;
  }
`;
const Cta = styled.span `
  ${ParagraphStyles.RegularStyles};
  ${fonts.matter.medium};
  position: relative;
  transition: color 1s ${animation.secondary};
  color: var(--textColor);

  ${Link}:hover & {
    color: ${brand.sage.sageLight};

    &:after {
      transform: scaleX(0.5);
    }
  }

  ${Link}:focus & {
    color: ${brand.sage.sageLight};
  }

  @media ${until(Device.Tablet)} {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 0;
  }

  @media ${from(Device.DesktopSmall)} {
    font-size: 20px;
    line-height: 28px;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: currentColor;
    transition: transform 1s ${animation.sharp};
    transform: scaleX(1);

    @media ${until(Device.Tablet)} {
      bottom: 0;
    }
  }
`;
const PlayButton = styled.div `
  --iconFg: ${brand.sage.sageDark};
  --iconBg: ${brand.white};

  position: absolute;
  top: 0;
  left: 0;
  display: grid;
  background: none;
  border: 0;
  height: 100%;
  margin: 0;
  padding: 0;
  place-content: center;
  place-items: center;
  width: 100%;
  z-index: 3;

  ${Link}:focus &,
  ${Link}:hover & {
    --iconBg: ${brand.sage.sageDark};
    --iconFg: ${brand.white};
  }
`;
const PlayIcon = styled(SvgPlayIcon) `
  display: block;
  height: 75px;
  transform: scale(1);
  transition: transform 0.15s ${animation.sharp};
  width: 75px;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  circle,
  path {
    transition: fill 0.15s ${animation.sharp}, stroke 0.15s ${animation.sharp};
  }

  ${Link}:focus &,
  ${Link}:hover & {
    transform: scale(1.2);
  }
`;
const MediaCardStyles = {
    Badge: StyledBadge,
    Container,
    ContentWrapper,
    Cta,
    Date,
    Image,
    ImageWrapper,
    Link,
    Metadata,
    PlayButton,
    PlayIcon,
    Summary,
    Title,
};
export default MediaCardStyles;
